import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import { Comment, Descriptions } from 'antd';
import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ApprovalStatus, CreateApprovalPayload } from 'api/approval';
import { DateType, ProjectId } from 'api/models';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { TaskAssigneePicker } from 'components/molecules/squad-picker/assignee-picker';
import { Option } from 'components/molecules/squad-picker/types';
import { CallToActions } from 'components/organisms/approvals/approval/approval-cta';
import styles from 'components/organisms/approvals/approval/approval.module.css';
import { MessageEditor } from 'components/organisms/approvals/approval/message-editor';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';

interface ApprovalActions {
  isLoading: boolean;
  onOk: () => void;
  onCancel: () => void;
}

const ApprovalActions: FC<ApprovalActions> = (props) => {
  const { onOk, onCancel, isLoading } = props;

  return (
    <div className={styles.cta__wrapper}>
      <Button
        loading={isLoading}
        type="primary"
        size="small"
        onClick={onOk}
        icon={<ApproveOutline16 />}
      >
        <FormattedMessage id="approvals.approve" />
      </Button>
      <Button
        className={styles.cta__cancel}
        type="default"
        size="small"
        onClick={onCancel}
        disabled={isLoading}
      >
        <FormattedMessage id="approvals.change_required" />
      </Button>
    </div>
  );
};

interface NewApprovalCardProps {
  projectId: number;
  isPrivate: boolean;
  isLoading: boolean;
  onCancel?: () => void;
  onSubmit: (arg: CreateApprovalPayload) => void;
  isFromDocument: boolean;
}

export const NewApprovalCard: FC<NewApprovalCardProps> = (props) => {
  const {
    projectId,
    isPrivate,
    onSubmit,
    onCancel,
    isLoading,
    isFromDocument,
  } = props;

  const { hasAccessToResource } = useWithAccess();
  const projectResource = useProjectAsRBACResource(projectId);

  const hasAccessToCreatePrivateApproval =
    hasAccessToResource({
      actionScope: ['PRIVATE'],
      rule: 'TASK_REQUEST_APPROVAL:WRITE',
      resourceData: projectResource,
    }) || isPrivate;

  const hasAccessToCreateSharedApproval = hasAccessToResource({
    actionScope: ['SHARED'],
    rule: 'TASK_REQUEST_APPROVAL:WRITE',
    resourceData: projectResource,
  });

  const hasAccessToCreatePrivateApprovalOnly =
    hasAccessToCreatePrivateApproval && !hasAccessToCreateSharedApproval;
  const hasAccessToCreateSharedApprovalOnly =
    hasAccessToCreateSharedApproval && !hasAccessToCreatePrivateApproval;
  const hasAccessToToggleApprovalType =
    hasAccessToCreatePrivateApproval && hasAccessToCreateSharedApproval;

  const { user } = useUser();
  const translatedCommentPlaceholderText = useTranslation(
    'approvals.comment.placeholder',
  );

  const [approvers, setApprovers] = useState<Option[]>([]);
  const [dueDate, setDueDate] = useState<DateType | null>(null);
  const [message, setMessage] = useState('');
  const [approvalIsPrivate, setApprovalIsPrivate] = useState(
    isPrivate || hasAccessToCreatePrivateApprovalOnly,
  );

  const handleApproversChange = (latest) => {
    setApprovers(latest);
  };

  useEffect(() => {
    if (approvalIsPrivate) {
      setApprovers([]);
    }
  }, [approvalIsPrivate]);

  const handleRequestApproval = () => {
    onSubmit({
      dueDate,
      private: approvalIsPrivate,
      requestBy: user,
      requestTo: approvers.map((option) => ({
        userId: option.extra.id,
        approvalStatus: ApprovalStatus.ApprovalRequested,
      })),
      comment: { content: message },
    });
  };

  const handleCancelApproval = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Comment
      avatar={
        <Avatar
          name={fullName(user)}
          shape="square"
          src={user.photoUrl}
          companyLogoUrl={user.company?.companyLogo}
        />
      }
      author={<strong>{fullName(user)}</strong>}
      content={
        <>
          <div className="caption text-03">
            <FormattedMessage
              id={
                approvalIsPrivate || hasAccessToCreatePrivateApprovalOnly
                  ? 'approvals.private.requesting'
                  : 'approvals.shared.requesting'
              }
            />
          </div>

          {hasAccessToCreateSharedApprovalOnly ? null : (
            <Descriptions>
              <Descriptions.Item
                className={styles.field}
                label={
                  <span className="label text-02">
                    <FormattedMessage id="type" />
                  </span>
                }
              >
                <Button
                  disabled={isPrivate || hasAccessToCreatePrivateApprovalOnly}
                  size="small"
                  icon={
                    approvalIsPrivate ||
                    hasAccessToCreatePrivateApprovalOnly ? (
                      <ViewOff16 />
                    ) : (
                      <View16 />
                    )
                  }
                  type="text"
                  onClick={() => {
                    if (!hasAccessToToggleApprovalType) {
                      return;
                    }
                    setApprovalIsPrivate(!approvalIsPrivate);
                  }}
                >
                  <FormattedMessage
                    id={approvalIsPrivate ? 'private' : 'public'}
                  />
                </Button>
              </Descriptions.Item>
            </Descriptions>
          )}

          <Descriptions colon={false}>
            <Descriptions.Item
              className={styles.field}
              label={
                <span className="label required text-02">
                  <FormattedMessage id="approvals.approvers" />
                </span>
              }
            >
              <TaskAssigneePicker
                projectId={ProjectId(projectId)}
                onChange={handleApproversChange}
                isPrivate={approvalIsPrivate}
                value={approvers}
                hidePlaceholders
                hidePlaceholderSection
                dueDate={dueDate}
                notIncludeRestrictedPartnerUser={isFromDocument}
              />
            </Descriptions.Item>
          </Descriptions>
          <Descriptions colon={false}>
            <Descriptions.Item
              className={styles.field}
              label={
                <span className="label text-02">
                  <FormattedMessage id="due_date" />
                </span>
              }
            >
              <div className={styles.due_date}>
                <CustomDueDatePicker
                  canChooseStartDate={false}
                  dueDate={dueDate}
                  onDateChange={([, latest]) => setDueDate(latest)}
                />
              </div>
            </Descriptions.Item>
          </Descriptions>
          <MessageEditor
            projectId={ProjectId(projectId)}
            className={classNames({
              [styles.private]: approvalIsPrivate,
            })}
            placeholder={translatedCommentPlaceholderText}
            data={message}
            onChange={setMessage}
          />
          <CallToActions
            loading={isLoading}
            disableOk={!approvers.length}
            okTooltipTitle={
              !approvers.length && (
                <FormattedMessage id="approvals.approvers.required.message" />
              )
            }
            cancelText={<FormattedMessage id="cancel" />}
            onCancel={handleCancelApproval}
            onOk={handleRequestApproval}
            okText={<FormattedMessage id="approvals.request" />}
          />
        </>
      }
    />
  );
};
